import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { AliasManager, rememberAliases } from 'lib/asset-aliases';
import type { AssetAlias } from 'types/api-responses';
import { setTractAliasesAtom, tractHeaderAtom } from './atoms';
import styles from './tract-aliases-header.module.scss';

/**
 * What the tract wizard's header shows beside its title once the tract exists: its T-number, its
 * aliases as pills, and Add Alias. A tract that is still being created has no identity yet, so the
 * header shows nothing extra.
 * @return {JSX.Element | null} The tract's identity and alias controls.
 */
export function TractAliasesHeader() {
  const tract = useAtomValue(tractHeaderAtom);
  const setAliases = useSetAtom(setTractAliasesAtom);
  const tractId = tract?.id;

  // The one place an add or a remove on this page succeeds. The Tract Catalog reads its tags from the
  // search index, which shows the server's write only after its next refresh, so the catalog is told what
  // the server just answered and shows it until the index has caught up (see rememberAliases in
  // lib/asset-aliases/utils).
  const handleChange = useCallback(
    (aliases: AssetAlias[]) => {
      setAliases(aliases);
      if (tractId) rememberAliases('tract', tractId, aliases);
    },
    [setAliases, tractId]
  );

  if (!tract) return null;

  return (
    <div className={styles.identity}>
      <span className={styles.tractNumber}>{tract.name}</span>
      <AliasManager
        aliases={tract.aliases}
        aliasesPath={`/crm/tracts/${tract.id}/aliases`}
        recordLabel="tract"
        recordNumber={tract.name}
        onChange={handleChange}
      />
    </div>
  );
}
